/**
 * The Product updates walk (Phase 91, 91-09; UI-SPEC [A]). Pure, no React.
 *
 * A "Place" link on a Product updates row opens the studio with the list it
 * was clicked in: the rows that owe a Large file, in the order on screen at
 * the click. The list rides in router state (`{ walk: { ids, index }, from }`)
 * and is never re-read while the operator works. Save and next and N open the
 * next id, P the previous one, and the title shows "4 of 12".
 *
 * Router state stays small: at most 500 ids. A longer list keeps the 500
 * starting at the clicked row (a list of 500 or fewer is carried whole, so
 * the counter reads the row's own place in it).
 *
 * Router state is anything the history holds, so `readWalk` checks its shape
 * before use (T-91-38); the server still reads and authorizes each product.
 */
import { isStudioProductId } from './product-id';

/** The most ids router state carries. */
export const STUDIO_WALK_MAX = 500;

export interface StudioWalk {
  ids: string[];
  index: number;
}

/** The fields of a Product updates row the walk reads. */
export interface StudioWalkRow {
  productId: string;
  needs: ReadonlyArray<{ platenVariant: string | null | undefined }>;
}

/** True for a row that owes a Large file (Small is made from Large, never placed). */
export function owesLargeFile(row: StudioWalkRow): boolean {
  return row.needs.some((need) => need.platenVariant === 'Large');
}

/** The ids of the rows that owe a Large file, in screen order. */
export function largeWalkIds(rows: readonly StudioWalkRow[]): string[] {
  return rows.filter(owesLargeFile).map((row) => row.productId);
}

/** The walk for a click on `clickedId` over ids already in screen order; null when it is not in them. */
export function walkFrom(ids: readonly string[], clickedId: string): StudioWalk | null {
  const at = ids.indexOf(clickedId);
  if (at < 0) return null;
  if (ids.length <= STUDIO_WALK_MAX) return { ids: [...ids], index: at };
  return { ids: ids.slice(at, at + STUDIO_WALK_MAX), index: 0 };
}

/** The walk a click on a Product updates row carries: the Large rows on screen, from the clicked one. */
export function buildWalk(rows: readonly StudioWalkRow[], clickedId: string): StudioWalk | null {
  return walkFrom(largeWalkIds(rows), clickedId);
}

/**
 * The walk in router state, or null for anything else: ids must be product
 * gids (1 to 500, no repeats) and the index a whole number inside them.
 */
export function readWalk(state: unknown): StudioWalk | null {
  const walk = (state as { walk?: unknown } | null | undefined)?.walk as { ids?: unknown; index?: unknown } | undefined;
  if (!walk || typeof walk !== 'object') return null;
  const { ids, index } = walk;
  if (!Array.isArray(ids) || ids.length === 0 || ids.length > STUDIO_WALK_MAX) return null;
  if (!ids.every((id) => isStudioProductId(id as string))) return null;
  if (new Set(ids).size !== ids.length) return null;
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index >= ids.length) return null;
  return { ids: ids as string[], index };
}

/** The walk at the open product: its own index when the state's index points elsewhere; null when it is not in the list. */
export function walkAt(walk: StudioWalk | null, productId: string | null): StudioWalk | null {
  if (!walk || !productId) return null;
  if (walk.ids[walk.index] === productId) return walk;
  const at = walk.ids.indexOf(productId);
  return at < 0 ? null : { ids: walk.ids, index: at };
}

/** The next step, or null after the last row. */
export function nextInWalk(walk: StudioWalk): StudioWalk | null {
  return walk.index + 1 < walk.ids.length ? { ids: walk.ids, index: walk.index + 1 } : null;
}

/** The previous step, or null on the first row. */
export function previousInWalk(walk: StudioWalk): StudioWalk | null {
  return walk.index > 0 ? { ids: walk.ids, index: walk.index - 1 } : null;
}

/** "4 of 12": the 1-based place and the total. */
export function walkCounter(walk: StudioWalk): { n: number; total: number } {
  return { n: walk.index + 1, total: walk.ids.length };
}

/** The studio's path for a product gid. */
export function studioPath(productId: string): string {
  return `/upload-studio/${encodeURIComponent(productId)}`;
}

/**
 * The print location the opening page was publishing to (router state
 * `locationId`), or null. Only a short plain id is taken; the studio uses it
 * only while it is one of the org's candidates (`useArtworkUploadLocation`).
 */
export function readStudioLocationId(state: unknown): string | null {
  const id = (state as { locationId?: unknown } | null | undefined)?.locationId;
  return typeof id === 'string' && id.length > 0 && id.length <= 64 && /^[\w-]+$/.test(id) ? id : null;
}
